import {
  Actions,
  ofActionCompleted,
  ofActionSuccessful,
  Select,
  Store
} from '@ngxs/store';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnDestroy
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ModalController } from '@ionic/angular';
import { combineLatest, filter, map, Observable, Subscription } from 'rxjs';
import _ from 'lodash';

import {
  AddDinerToTicket,
  AddDinerToTicketSuccess,
  CancelTicket,
  RemoveDinerFromTicket,
  RemoveGuestOfDinerFromTicket
} from 'src/app/store/tickets/tickets.action';
import {
  BarcodeReaderEventType,
  BarcodeResult,
  BaseDiner,
  NormalizedTicket,
  PendingAssignment,
  ReservationDiner,
  VisitingFacility
} from 'src/app/models';
import {
  ClearSearchDinersResult,
  GetDinerByBarcode,
  GetDiners,
  GetSearchingDinersSuccess,
  MultipleDinersFound,
  SelectDiner
} from 'src/app/store/diners/diners.action';
import {
  CommonConstant,
  DinerAssignmentView,
  ViewConstant
} from 'src/app/constants';
import { BarcodeProvider } from 'src/app/providers/barcode.provider';
import { LogRocketProvider } from 'src/app/providers/logrocket.provider';
import { ReactiveComponent } from 'src/app/components/shared/reactive-component';
import { ClosePendingSeatAssignment } from 'src/app/store/host-mode/host-mode.action';
import { NfcReaderService } from 'src/app/services/nfc-reader/nfc-reader.service';
import { HostModeSelectors } from 'src/app/store/host-mode/host-mode.selector';
import { TablesStateSelectors } from 'src/app/store/tables/tables.selectors';
import { TicketsStateSelectors } from 'src/app/store/tickets/tickets.selectors';
import { AppSelectors } from 'src/app/store/app/app.selectors';
import { DinersSelectors } from 'src/app/store/diners/diners.selectors';
import { LocationSelectors } from 'src/app/store/location/location.selectors';
import {
  ChargeGuestMealModal,
  ChargeGuestMealModalProps
} from '../../charge-guest-meal/charge-guest-meal.modal';
import { ModalService } from 'src/app/services';

export interface DinerAssignmentModalProps {
  residentsOnly?: boolean;
  cancelTicketOnClose?: boolean;
  isLoading?: boolean;
}

@Component({
  selector: 'pos-diner-assignment-modal',
  templateUrl: './diner-assignment.modal.html',
  styleUrls: ['./diner-assignment.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DinerAssignmentModal
  extends ReactiveComponent
  implements OnDestroy
{
  @Input() residentsOnly = false;
  @Input() cancelTicketOnClose = false;
  @Input() isLoading = true;

  @ViewSelectSnapshot(TicketsStateSelectors.normalizedTicket)
  readonly ticket: NormalizedTicket;

  @Select(DinersSelectors.filteredDiners) readonly diners$: Observable<
    BaseDiner[]
  >;
  @Select(DinersSelectors.assignedResidents)
  readonly assignedResidents$: Observable<BaseDiner[]>;
  @ViewSelectSnapshot(LocationSelectors.getIsDiningRoom)
  readonly isDiningRoom: boolean;
  @ViewSelectSnapshot(LocationSelectors.getIsServiceAppointment)
  readonly isServiceAppointment: boolean;
  @ViewSelectSnapshot(AppSelectors.visiting_facilities)
  readonly visitingFacilities: VisitingFacility[];
  @ViewSelectSnapshot(TablesStateSelectors.getReservationDinersOfSelectedTable)
  readonly reservationDiners: ReservationDiner[];
  @SelectSnapshot(AppSelectors.isInputFocused)
  private readonly isInputFocused: boolean;
  @SelectSnapshot(TicketsStateSelectors.selectedTicketId)
  private readonly _selectedTicketId: string;
  @ViewSelectSnapshot(TicketsStateSelectors.selectedProcessing)
  readonly isSelectedTicketProcessing: boolean;

  pendingAssignments: PendingAssignment[];

  //Preassigned view only available in DR
  view: DinerAssignmentView = ViewConstant.DINER_ASSIGNMENT_VIEW.LOOKUP;
  readonly DinerAssignmentView = DinerAssignmentView;
  visitingFacilityId: number;
  searchQuery: string;
  totalAssignedDiners: number;
  isSearchingDiner = false;

  assigningIndex: number;
  assigningHostMode = false;
  assigningReservation = false;

  readonly DINER_ASSIGNMENT_VIEW = ViewConstant.DINER_ASSIGNMENT_VIEW;
  private isSearchingByBarcode: boolean;
  private barcodeReaderSubscription: Subscription;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly modalController: ModalController,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly barcodeProvider: BarcodeProvider,
    private readonly logRocketProvider: LogRocketProvider,
    private readonly nfcService: NfcReaderService,
    private readonly modalService: ModalService
  ) {
    super();
  }

  *onSubscribe() {
    setTimeout(() => {
      this.isLoading = false;
    }, CommonConstant.MAX_LOADING_TIME);

    yield this.actions$
      .pipe(
        ofActionSuccessful(
          RemoveDinerFromTicket,
          AddDinerToTicketSuccess,
          RemoveGuestOfDinerFromTicket
        )
      )
      .subscribe(() => this.onCloseModal());

    yield this.actions$
      .pipe(
        ofActionSuccessful(SelectDiner),
        filter((input) => input.ticket?.diner_id && !input.addMealPlan)
      )
      .subscribe(() => this.onCloseModal());

    yield this.actions$
      .pipe(
        ofActionSuccessful(ClosePendingSeatAssignment),
        filter((input) => !input.dinerId)
      )
      .subscribe(() => this.onCloseModal());

    yield this.store
      .select(TicketsStateSelectors.selectedTicketId)
      .pipe(filter((ticket) => !!ticket && this.isLoading))
      .subscribe(() => (this.isLoading = false));

    yield this.actions$
      .pipe(ofActionSuccessful(MultipleDinersFound))
      .subscribe(({ diners, barcode }: MultipleDinersFound) => {
        this.isSearchingDiner = false;
        this.view = this.DINER_ASSIGNMENT_VIEW.LOOKUP;
        this.store.dispatch([
          new ClearSearchDinersResult(),
          new GetSearchingDinersSuccess(diners)
        ]);
        this.isSearchingByBarcode = true;
        this.searchQuery = barcode;
      });

    yield this.store
      .selectOnce(HostModeSelectors.getSelectedTablePendingAssignments)
      .subscribe(
        (res) =>
          // this take 1 because during the close assignment, the list data may changes, so we need to get the first data
          (this.pendingAssignments = res)
      );

    this.barcodeReaderSubscription = this.barcodeProvider.subscribe(
      BarcodeReaderEventType.PATIENT,
      (barcode: BarcodeResult) => {
        //If diner input is not focused, barcode is coming from hardware
        if (!this.isInputFocused && !!barcode.barcode) {
          this.logRocketProvider.log(
            '[Diner Assignment] BarcodeReaderEventType.PATIENT barcode',
            JSON.stringify(barcode)
          );
          this.onBarcodeScan(barcode.barcode);
        }
      }
    );

    yield combineLatest([
      this.store.selectOnce(DinersSelectors.assignedResidents),
      this.store.selectOnce(
        HostModeSelectors.getSelectedTablePendingAssignments
      ),
      this.store.selectOnce(
        TablesStateSelectors.getReservationDinersOfSelectedTable
      )
    ])
      .pipe(map((res) => _.sumBy(res, (o) => (_.isEmpty(o) ? 0 : o.length))))
      .subscribe((total) => {
        this.totalAssignedDiners = total;
        if (!this.isLoading && total > 0 && this.isDiningRoom) {
          this.view = this.DINER_ASSIGNMENT_VIEW.PREASSIGNED;
        } else if (this.isLoading || total === 0) {
          this.view = this.DINER_ASSIGNMENT_VIEW.LOOKUP;
        }
      });

    yield this.actions$
      .pipe(ofActionCompleted(GetDiners, ClearSearchDinersResult))
      .subscribe(() => (this.isSearchingDiner = false));

    // Read NFC Tag - Android
    // Once the reader mode is enabled, any tags that are scanned are sent to the subscriber
    this.nfcService.startNFC(({ data }) => {
      console.log('NFC Reader Service', data);
      this.onBarcodeScan(data);
    });
  }

  onSetView(view: DinerAssignmentView) {
    //Reset Visiting Facility when changing view
    if (view !== ViewConstant.DINER_ASSIGNMENT_VIEW.OTHER_FACILITIES) {
      this.visitingFacilityId = null;
    }

    //Clear searched diners
    if (view !== ViewConstant.DINER_ASSIGNMENT_VIEW.PREASSIGNED) {
      this.store.dispatch(new ClearSearchDinersResult());
    }
    //Clear search query
    this.searchQuery = null;
    this.view = view;
  }

  blurActiveElement() {
    (this.document.activeElement as HTMLElement)?.blur();
  }

  onAssignDiner(
    item: BaseDiner | PendingAssignment | ReservationDiner,
    index: number,
    isHostMode = false,
    isReservation = false
  ) {
    if (!_.isNil(this.assigningIndex)) {
      return;
    }

    this.assigningIndex = index;
    this.assigningHostMode = isHostMode;
    this.assigningReservation = isReservation;

    if ((isHostMode || isReservation) && 'diner_id' in item) {
      const { diner_id, id } = item;
      if (diner_id) {
        if (isHostMode) {
          this.store.dispatch(new ClosePendingSeatAssignment(id, diner_id));
        }

        this.store.dispatch(
          new AddDinerToTicket({
            diner: {
              id: diner_id,
              name: 'name' in item ? item.name : item.diner_name
            },
            device_ticket_uuid: this._selectedTicketId
          })
        );
      } else {
        const hasDinerAssigned = this.ticket?.diner_id;
        if (hasDinerAssigned) {
          this.store.dispatch(
            new RemoveDinerFromTicket(this.ticket.device_ticket_uuid)
          );
        }

        if (isHostMode) {
          this.store.dispatch(new ClosePendingSeatAssignment(id));
        }
      }
      return;
    }

    this.store.dispatch(
      new AddDinerToTicket({
        diner: item,
        device_ticket_uuid: this._selectedTicketId
      })
    );
  }

  async onSeatGuestClicked() {
    const isAllowChargeGuest = this.store.selectSnapshot(
      LocationSelectors.getGuestChargeResidentsMealPlan
    );

    if (isAllowChargeGuest) {
      const chargeGuestModal = await this.modalService.showModal<
        void,
        ChargeGuestMealModalProps
      >(ChargeGuestMealModal, {
        isLoading: false,
        cancelTicketOnClose: this.cancelTicketOnClose
      });
      await this.onCloseModal();
      await chargeGuestModal.present();
    } else {
      this.onCloseModal();
    }
  }

  onRemoveDiner(isGuestOfDiner = false) {
    const ticketUuid = this.ticket.device_ticket_uuid;

    this.store.dispatch(
      isGuestOfDiner
        ? new RemoveGuestOfDinerFromTicket(ticketUuid)
        : new RemoveDinerFromTicket(ticketUuid)
    );
  }

  // async onCloseModal(noAssignmentDone = false) {
  async onCloseModal(noAssignmentDone = false) {
    await this.clearSearchDinersAndCancelTicket(noAssignmentDone);
    await this.modalController.dismiss();
  }

  async clearSearchDinersAndCancelTicket(noAssignmentDone = false) {
    this.assigningIndex = undefined;
    this.isSearchingDiner = false;
    this.assigningHostMode = false;
    this.assigningReservation = false;
    const actions = [new ClearSearchDinersResult()];
    if (this.cancelTicketOnClose && noAssignmentDone) {
      actions.push(new CancelTicket(this._selectedTicketId));
    }
    this.store.dispatch(actions);
  }

  searchingDiners(event: Event): void {
    this.isSearchingDiner = true;
    if (this.isSearchingByBarcode) {
      this.isSearchingByBarcode = false;
      return;
    }
    this.searchQuery =
      (event as CustomEvent<{ value: string }>).detail.value || '';
    //this is cheat to prevent the ionChange call two times
    if (!this.searchQuery || !this.searchQuery.length) {
      this.store.dispatch(new ClearSearchDinersResult());
    }

    if (
      !this.searchQuery ||
      event.defaultPrevented ||
      this.searchQuery.length <= 2
    ) {
      this.isSearchingDiner = false;
      return;
    }

    event.preventDefault();
    this.store.dispatch(
      new GetDiners({
        name: this.searchQuery,
        patients_only: this.residentsOnly,
        visiting_facility_id: this.visitingFacilityId
      })
    );
  }

  onFacilityChange(event: any) {
    this.visitingFacilityId = event.detail.value;
  }

  onBarcodeScan(barcodeData: string) {
    this.store.dispatch(new GetDinerByBarcode(barcodeData, 'ticketAssignment'));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.barcodeProvider.unsubscribe(this.barcodeReaderSubscription);
    this.nfcService.stopNFC();
  }
}
